import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { StateValues } from 'react-use-form-state';
import {
  AdminApi,
  AdminOrganizationDetails,
  AdminOrganizationRequest,
  AdminOrganizationsServiceContractsDestroyRequest,
  AdminOrganizationsServiceContractsUpdateRequest,
} from 'api';
import useApiCall from 'hooks/use-api-call';
import { makeModalForm } from 'ui/molecules/modal';
import AddOrganizationModal, { AddOrganizationFields } from '../add-organization-modal';

interface EditOrganizationProps {
  onEditOrganization: () => void;
  onClose: () => void;
  data: AdminOrganizationDetails;
}

export const Form = makeModalForm<AddOrganizationFields>();

const EditOrganization: FunctionComponent<EditOrganizationProps> = ({ onEditOrganization, onClose, data }) => {
  const [cumulativeEntryError, setCumulativeEntryError] = useState(false);
  const [singleEntryError, setSingleEntryError] = useState(false);

  const { makeAuthenticatedApi, withApi, error } = useApiCall();

  const api: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const onSubmit = useCallback((fieldsValues: StateValues<AddOrganizationFields>) => {
    setCumulativeEntryError(false);
    setSingleEntryError(false);

    const params: AdminOrganizationRequest = {
      name: fieldsValues.name,
      loginType: fieldsValues.loginType,
    };

    withApi(async () => {
      try {
        await api.adminOrganizationsPartialUpdate({ id: data.id, patchedAdminOrganizationRequest: params });

        const serviceContractsToAdd = fieldsValues.serviceContracts.filter(
          (item) => !data.serviceContracts.includes(item),
        );
        for (const serviceContract of serviceContractsToAdd) {
          const serviceContractParams: AdminOrganizationsServiceContractsUpdateRequest = {
            id: data.id,
            techName: serviceContract,
            adminOrganizationRequest: {
              name: fieldsValues.name,
            },
          };
          await api.adminOrganizationsServiceContractsUpdate(serviceContractParams);
        }

        const serviceContractsToDelete = data.serviceContracts.filter(
          (item) => !fieldsValues.serviceContracts.includes(item),
        );
        for (const serviceContract of serviceContractsToDelete) {
          const serviceContractParams: AdminOrganizationsServiceContractsDestroyRequest = {
            id: data.id,
            techName: serviceContract,
          };
          await api.adminOrganizationsServiceContractsDestroy(serviceContractParams);
        }

        onEditOrganization();
        onClose();
      } catch (err: any) {
        setCumulativeEntryError(err.getErrorCode('cumulative_entry_required_but_missing'));
        setSingleEntryError(err.getErrorCode('single_entry_required_but_missing'));
      }
    });
  }, []);

  return (
    <AddOrganizationModal
      onSubmit={onSubmit}
      onClose={onClose}
      error={error}
      cumulativeEntryRequiredButMissing={cumulativeEntryError}
      singleEntryRequiredButMissing={singleEntryError}
      data={data}
    />
  );
};

export default EditOrganization;
